import * as React from "react";
import {useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {AnyThunkDispatch} from "../../../commonTypes";
import {IClientReportDocument} from "../../model/ClientReport.model";
import {SharedDocumentApi} from "../../api/SharedDocumentApi";
import {formatShortDate} from "../../utils/dateUtil";
import {getFileExtension} from "../../utils/fileUtil";
import {hasConsultantLevelAccess} from "../../utils/sessionUtil";
import {PowerBiReportingApi} from "../../api/PowerBiReportingApi";
import {setHeaderNotification} from "../base/header/HeaderActions";
import {NotificationTypes} from "../base/header/HeaderReducer";
import {DialogComponent} from "../common/Dialog.component";

export interface ISupportingDocumentsProps {
    documents: IClientReportDocument[];
    reportId: number;
}

export const NO_SUPPORTING_DOCUMENTS_TEXT = "No supporting documents have been attached to this report.";
export const ATTACHMENT_FAILURE_TEXT = "Failed to attach the document. Please try again.";
export const REMOVE_FAILURE_TEXT = "Failed to remove the document. Please try again.";
export const buildRemoveSuccessText = (title: string) => `${title} removed from report.`;
export const SupportingDocuments: React.FunctionComponent<ISupportingDocumentsProps> = (props) => {
    const SECTION_ID = "supporting-documents__list";

    const dispatch: AnyThunkDispatch = useDispatch();
    const [isExpanded, setIsExpanded] = useState(false);
    const [localDocuments, setLocalDocuments] = useState<IClientReportDocument[]>(props.documents);
    const [isAttachFormOpen, setIsAttachFormOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);
    const [documentTitle, setDocumentTitle] = useState("");
    const [isUploading, setIsUploading] = useState(false);
    const [documentToRemove, setDocumentToRemove] = useState<IClientReportDocument | undefined>(undefined);
    const [isRemoving, setIsRemoving] = useState(false);

    const getFileTitle = (fileName: string): string => {
        const lastIndex = fileName.lastIndexOf(".");
        return lastIndex < 0 ? fileName : fileName.substring(0, lastIndex);
    };


    useEffect(() => {
        setLocalDocuments(props.documents);
        setIsAttachFormOpen(false);
        setSelectedFile(undefined);
        setDocumentTitle("");
        setDocumentToRemove(undefined);
    }, [props.documents]);

    const closeRemoveDialog = () => setDocumentToRemove(undefined);

    const handleRemoveConfirm = () => {
        if (!documentToRemove) return;
        const target = documentToRemove;
        setIsRemoving(true);
        PowerBiReportingApi.removeDocumentFromClientReport(props.reportId, target.id)
            .then(() => {
                setLocalDocuments((prev) => prev.filter((d) => d.id !== target.id));
                dispatch(setHeaderNotification(
                    {message: buildRemoveSuccessText(target.title!), notificationType: NotificationTypes.SUCCESS},
                    5000
                ));
            })
            .catch(() => {
                dispatch(setHeaderNotification(
                    {message: REMOVE_FAILURE_TEXT, notificationType: NotificationTypes.FAILURE},
                    5000
                ));
            })
            .finally(() => {
                setIsRemoving(false);
                closeRemoveDialog();
            });
    };

    const toggleExpanded = () => setIsExpanded((previous) => !previous);

    const getDocumentTypeLabel = (fileName: string): string => {
        const extension = getFileExtension(fileName);
        return extension && extension !== fileName ? extension.toUpperCase() : "";
    };

    const handleFileChosen = (files: FileList | null) => {
        if (!files || files.length === 0) return;
        const file = files[0];
        setSelectedFile(file);
        setDocumentTitle(getFileTitle(file.name));
    };

    const closeAttachForm = () => {
        setIsAttachFormOpen(false);
        setSelectedFile(undefined);
        setDocumentTitle("");
    };

    const handleAttach = () => {
        if (!selectedFile || !documentTitle.trim()) return;
        setIsUploading(true);
        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("title", documentTitle.trim());
        PowerBiReportingApi.addDocumentToClientReport(props.reportId, formData)
            .then((result) => {
                if (result.data) {
                    setLocalDocuments((prev) => [result.data!, ...prev]);
                    closeAttachForm();
                }
            })
            .catch(() => {
                dispatch(setHeaderNotification(
                    {message: ATTACHMENT_FAILURE_TEXT, notificationType: NotificationTypes.FAILURE},
                    5000
                ));
            })
            .finally(() => setIsUploading(false));
    };

    const renderEmptyState = () => (
        <div className="supporting-documents__empty" data-testid="supporting-documents__empty">
            {NO_SUPPORTING_DOCUMENTS_TEXT}
        </div>
    );

    const renderDocumentRow = (document: IClientReportDocument) => {
        const typeLabel = getDocumentTypeLabel(document.fileName);
        const displayTitle = document.title || document.fileName;
        return (
            <li key={document.id} className="supporting-documents__item">
                <a className="supporting-documents__link old-anchor"
                   data-testid="supporting-documents__link"
                   onClick={() => SharedDocumentApi.getDocumentDownload(document.id, document.fileName, true)}>
                    {displayTitle}
                </a>
                <span className="supporting-documents__meta">
                    <span className="supporting-documents__type"
                          data-testid="supporting-documents__type">{typeLabel}</span>
                    <span className="supporting-documents__date"
                          data-testid="supporting-documents__date">{formatShortDate(document.uploadDate)}</span>
                </span>
                {hasConsultantLevelAccess() && (
                    <button type="button"
                            className="supporting-documents__remove"
                            data-testid={`supporting-documents__remove-${document.id}`}
                            aria-label={`Remove ${displayTitle} from the report`}
                            onClick={() => setDocumentToRemove(document)}>
                        <i className="fa-light fa-xmark" aria-hidden="true"/>
                    </button>
                )}
            </li>
        );
    };

    const renderAttachForm = () => (
        <div className="supporting-documents__attach-form" data-testid="supporting-documents__attach-form">
            <h4 className="supporting-documents__attach-form-title">Attach a supporting document</h4>
            <div className="supporting-documents__attach-form-fields">
                <div className="supporting-documents__attach-file-col">
                    <label className="supporting-documents__attach-file-label">File</label>
                    <input
                        id="attach-file-input"
                        type="file"
                        data-testid="supporting-documents__file-input"
                        className="supporting-documents__file-input"
                        disabled={isUploading}
                        onChange={(e) => handleFileChosen(e.target.files)}
                    />
                    <span className="supporting-documents__file-hint">PDF, Excel, Word or CSV · max 50 MB</span>
                </div>
                <div className="supporting-documents__title-col">
                    <label htmlFor="attach-title-input" className="supporting-documents__attach-title-label">
                        Document title
                    </label>
                    <input
                        id="attach-title-input"
                        type="text"
                        data-testid="supporting-documents__title-input"
                        className="supporting-documents__title-input"
                        placeholder={!selectedFile ? "Choose a file first" : ""}
                        disabled={!selectedFile || isUploading}
                        value={documentTitle}
                        onChange={(e) => setDocumentTitle(e.target.value)}
                    />
                    {!selectedFile && (
                        <span className="supporting-documents__title-hint">Editable after you choose a file</span>
                    )}
                </div>
            </div>
            <div className="supporting-documents__attach-buttons">
                <button
                    type="button"
                    className="supporting-documents__attach-button"
                    data-testid="supporting-documents__attach-button"
                    onClick={handleAttach}
                    disabled={!selectedFile || !documentTitle.trim() || isUploading}
                >
                    {isUploading
                        ? <span className="supporting-documents__spinner" data-testid="supporting-documents__spinner" aria-label="Uploading"/>
                        : "Attach"}
                </button>
                <button
                    type="button"
                    className="supporting-documents__cancel-button"
                    data-testid="supporting-documents__cancel-button"
                    onClick={closeAttachForm}
                    disabled={isUploading}
                >
                    Cancel
                </button>
            </div>
        </div>
    );

    return (
        <section className="supporting-documents" data-testid="supporting-documents">
            <div className="supporting-documents__toggle">
                <button type="button"
                        className="supporting-documents__title"
                        data-testid="supporting-documents__toggle"
                        aria-expanded={isExpanded}
                        aria-controls={SECTION_ID}
                        onClick={toggleExpanded}>
                    Supporting Documents
                </button>
                <span className="supporting-documents__count"
                      data-testid="supporting-documents__count"
                      aria-label={`${localDocuments.length} documents`}
                      onClick={toggleExpanded}>
                    {localDocuments.length}
                </span>
                <i className={`supporting-documents__chevron fa-light ${isExpanded ? "fa-angle-up" : "fa-angle-down"}`}
                   aria-hidden="true"
                   onClick={toggleExpanded}/>
            </div>
            {isExpanded && (
                <div id={SECTION_ID}
                     className="supporting-documents__panel"
                     data-testid="supporting-documents__panel">
                    {localDocuments.length === 0
                        ? renderEmptyState()
                        : (
                            <ul className="supporting-documents__list">
                                {localDocuments.map(renderDocumentRow)}
                            </ul>
                        )}
                </div>
            )}
            {hasConsultantLevelAccess() && !isAttachFormOpen && (
                <a className="supporting-documents__attach-link old-anchor"
                   data-testid="supporting-documents__attach-link"
                   onClick={() => setIsAttachFormOpen(true)}>
                    + Attach file
                </a>
            )}
            {isAttachFormOpen && renderAttachForm()}
            <DialogComponent
                open={!!documentToRemove}
                title="Remove this file from the report?"
                description={
                    <span data-testid="supporting-documents__remove-description">
                        <strong>{documentToRemove?.title ?? ""}</strong>
                        {" will no longer appear as a supporting document."}
                    </span>
                }
                yesText={isRemoving ? "Removing…" : "Yes, remove"}
                noText="No, cancel"
                onYes={handleRemoveConfirm}
                onNo={closeRemoveDialog}
            />
        </section>
    );
};
